import { connect } from "react-redux";
import { useEffect, useState } from "react";
import getConfig from "next/config";
import classNames from "classnames";

import NoStoreSelected from "@components/ProductPage/ProductStoreAvailability/NoStoreSelected";
import AvailableStoresModal from "@components/ProductPage/ProductStoreAvailability/AvailableStoresModal";
import { getProductStoreAvailability } from "@store/catalog/actions";
import StoreSelected from "@components/ProductPage/ProductStoreAvailability/StoreSelected";
import { removeModal } from "@store/app/actions";
import { localClientStore } from "@lib/clientStore";
import Loading from "@components/Loading";
import { setFavouriteStoreId } from "@store/user/actions";
import StoreSelectModal from "@components/ProductPage/ProductStoreAvailability/StoreSelectModal";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

const ProductStoreAvailability = ({
  productStoreAvailability,
  user,
  getProductStoreAvailability,
  identifier,
  removeModal,
  stores,
  setFavouriteStoreId,
  openedModal,
}) => {
  const { data: product, isLoading } = productStoreAvailability;
  const { customer, favouriteStoreId, isLoggedIn } = user;

  // Stores without stock are left out of the availability payload, so a store
  // that carries no qty means "out of stock" only once this product's stockInfo
  // has actually landed. Until then (loading, request failed, or the payload
  // still belongs to the previously viewed sku) the stock state is unknown and
  // no status is shown.
  const isStockKnown =
    product?.sku === identifier && Array.isArray(product.stockInfo);

  const [selectedStore, setSelectedStore] = useState(null);

  // Determine the selected store ID from customer, favorite, or local storage
  const selectedStoreId =
    customer?.store_id ??
    favouriteStoreId ??
    localClientStore.get("favouriteStoreId");

  useEffect(() => {
    const resolveSelectedStore = () => {
      if (selectedStoreId && stores?.length) {
        // Find the selected store in the stores array
        const selectedStore = stores.find(
          (store) => store.id === selectedStoreId
        );

        if (!product?.stockInfo) {
          setSelectedStore(selectedStore);
          return;
        }

        // Find the selected store in product stock availability
        const storeWithStock = product.stockInfo.find(
          (store) => store.id === selectedStoreId
        );

        // Update the selected store with stock info or fallback to the selected store
        setSelectedStore(storeWithStock || selectedStore);
      }
    };

    resolveSelectedStore();
  }, [
    product,
    isLoggedIn,
    customer,
    favouriteStoreId,
    stores,
    selectedStoreId,
  ]);

  useEffect(() => {
    if (!product || product.sku !== identifier) {
      getProductStoreAvailability(identifier);
    }
  }, [identifier]);

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setFavouriteStoreId(store.id);

    removeModal();
  };

  if (isLoading)
    return (
      <div
        className={classNames("py-[40px] px-[20px]", {
          "bg-[#FAFAFA]": !isBricoStore,
          "border rounded-[4px] border-gray-light": isBricoStore,
        })}
      >
        <Loading hasMarginBottom={false} />
      </div>
    );

  return (
    <div
      className={classNames("p-[16px] rounded-[4px]", {
        "bg-[#FAFAFA]": !isBricoStore,
        "border border-gray-light": isBricoStore,
      })}
    >
      {selectedStore ? (
        <StoreSelected storeData={selectedStore} isStockKnown={isStockKnown} />
      ) : (
        <NoStoreSelected />
      )}

      {openedModal === "AvailableStoresModal" && (
        <AvailableStoresModal
          product={product}
          onStoreSelect={handleStoreSelect}
          selectedStore={selectedStore}
        />
      )}

      {openedModal === "StoreSelectModal" && (
        <StoreSelectModal
          onStoreSelect={handleStoreSelect}
          selectedStore={selectedStore}
        />
      )}
    </div>
  );
};

export default connect(
  (state) => ({
    productStoreAvailability: state.catalog.productStoreAvailability,
    user: state.user,
    stores: state.stores.data,
    openedModal: state.app.modal,
  }),
  { getProductStoreAvailability, removeModal, setFavouriteStoreId }
)(ProductStoreAvailability);
